import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { initialState } from './initialState';
import { ApiExError, apiReponseError, apiReponseErrorIncludes } from '@shared';
const advisorAdminPortalSlice = createSlice({
  name: 'advisorAdminPortal',
  initialState: initialState.advisorAdminPortal,
  reducers: {
    postWizardAdvisorFieldUpdateByAdvisorIdRequest: (
      state,
      _action: PayloadAction<AdvisorFieldUpdateProps>,
    ) => {
      state.status = 'loading';
      state.errorMessage = null;
      state.successMessage = null;
    },
    postWizardAdvisorFieldUpdateByAdvisorIdResponse: (state, action: PayloadAction<string>) => {
      if (action.payload === 'Advisor not found') {
        state.errorMessage = action.payload;
        state.status = 'error';
      } else if (action.payload === 'Invalid email update method') {
        state.errorMessage =
          "Can't update advisor email, advisor is using Google or Microsoft as their login method.";
        state.status = 'error';
      } else {
        state.status = 'success';
        state.successMessage = action.payload;
      }
    },
    postWizardAdvisorFieldUpdateByAdvisorIdFailure: (state, action: PayloadAction<ApiExError>) => {
      if (action?.payload?.statusCode === '409') {
        state.errorMessage = 'An advisor with this email already exists!';
      } else {
        state.errorMessage = 'An unknown error occurred';
      }
      state.status = 'error';
    },
    resetAdvisorAdminPortal: state => {
      state.status = null;
      state.errorMessage = null;
      state.successMessage = null;
      state.advisorFetchStatus = null;
      state.advisorFetchError = null;
      state.selectedAdvisor = null;
    },
    postWizardAdvisorForAdminSearchRequest: (
      state,
      _action: PayloadAction<{ advisorId?: string; advisorEmail?: string }>,
    ) => {
      state.advisorFetchStatus = 'loading';
      state.advisorFetchError = null;
      state.selectedAdvisor = null;
    },
    postWizardAdvisorForAdminSearchResponse: (
      state,
      action: PayloadAction<{ advisor: Advisor; clientCount: number }>,
    ) => {
      state.advisorFetchStatus = 'success';
      state.selectedAdvisor = action.payload.advisor;
      state.advisorFetchError = null;
    },
    postWizardAdvisorForAdminSearchFailure: (state, _action: PayloadAction<ApiExError>) => {
      state.advisorFetchStatus = 'error';
      state.selectedAdvisor = null;
      state.advisorFetchError = 'Advisor not found';
    },
    postWizardAdvisorReportingAttributeByAdvisorIdRequest: (
      state,
      _action: PayloadAction<AdvisorReportingAttributeProps>,
    ) => {
      state.status = 'loading';
      state.errorMessage = null;
      state.successMessage = null;
    },
    postWizardAdvisorReportingAttributeByAdvisorIdFailure: (state, action: PayloadAction<ApiExError>) => {
      state.status = 'error';
      if (apiReponseErrorIncludes(action.payload, 'advisor not found')) {
        state.errorMessage = 'Advisor ID not found';
      } else if (apiReponseErrorIncludes(action.payload, 'create failed')) {
        state.errorMessage = 'Attribute already exists. Please use the update or delete action.';
      } else if (apiReponseErrorIncludes(action.payload, 'update failed')) {
        const errorMessage = apiReponseError(action.payload);
        state.errorMessage = errorMessage;
      } else if (apiReponseErrorIncludes(action.payload, 'delete failed')) {
        state.errorMessage = 'Delete failed. Attribute does not exist.';
      } else {
        state.errorMessage = 'An unknown error occurred';
      }
    },
    postWizardAdvisorReportingAttributeByAdvisorIdResponse: (state, action: PayloadAction<string>) => {
      state.status = 'success';
      state.successMessage = action.payload;
    },
  },
});

export const {
  postWizardAdvisorFieldUpdateByAdvisorIdRequest,
  postWizardAdvisorFieldUpdateByAdvisorIdResponse,
  postWizardAdvisorFieldUpdateByAdvisorIdFailure,
  postWizardAdvisorReportingAttributeByAdvisorIdRequest,
  postWizardAdvisorReportingAttributeByAdvisorIdResponse,
  postWizardAdvisorReportingAttributeByAdvisorIdFailure,
  postWizardAdvisorForAdminSearchRequest,
  postWizardAdvisorForAdminSearchResponse,
  postWizardAdvisorForAdminSearchFailure,
  resetAdvisorAdminPortal,
} = advisorAdminPortalSlice.actions;

export const advisorAdminPortalReducer = advisorAdminPortalSlice.reducer;
